import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { GoogleSignInButton, OAuthDivider, MagicLinkButton, PasswordlessBanner } from "../components/Auth";

// Check if Google OAuth is configured
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";

const Login = () => {
  const [oauthLoading, setOauthLoading] = useState(false);

  const { loginWithGoogle, clearError } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || "/dashboard";

  const handleGoogleSuccess = async (response) => {
    setOauthLoading(true);
    try {
      const result = await loginWithGoogle(response);
      if (result.success) {
        // If super admin, redirect to admin dashboard
        if (result.isPlatformAdmin || result.redirectUrl === "/admin") {
          navigate("/admin", { replace: true });
        } else {
          navigate(result.redirectUrl || from, { replace: true });
        }
      }
    } finally {
      setOauthLoading(false);
    }
  };

  const handleGoogleError = (errorMessage) => {
    // Error is handled by AuthContext and displayed via error state
    console.error("Google login error:", errorMessage);
  };

  const handleMagicLinkSuccess = (response) => {
    // Magic link sent successfully - component handles the success UI
    clearError();
  };

  const handleMagicLinkError = (errorMessage) => {
    // Error is displayed by the MagicLinkButton component
    console.error("Magic link error:", errorMessage);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-purple-600 rounded-lg flex items-center justify-center">
            <span className="text-white text-xl font-bold">DZ</span>
          </div>
        </div>
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          Welcome Back
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Don't have an account?{" "}
          <Link
            to="/register"
            className="font-medium text-primary-600 hover:text-primary-500"
          >
            Sign up
          </Link>
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {/* Passwordless Migration Banner */}
          <PasswordlessBanner className="mb-6" />

          {/* Magic Link Login - Primary Method with Visual Emphasis */}
          <div className="relative">
            {/* "Recommended" badge */}
            <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-10">
              <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-gradient-to-r from-green-500 to-emerald-600 text-white shadow-md">
                <svg
                  className="w-3.5 h-3.5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                  />
                </svg>
                Recommended
              </span>
            </div>

            {/* Highlighted container for magic link */}
            <div className="border-2 border-primary-200 bg-primary-50 rounded-lg p-4 pt-6">
              <div className="text-center mb-3">
                <h3 className="text-sm font-semibold text-gray-900 mb-1">
                  Sign in with Magic Link
                </h3>
                <p className="text-xs text-gray-600">
                  Faster and more secure - no password needed
                </p>
              </div>
              <MagicLinkButton
                mode="login"
                onSuccess={handleMagicLinkSuccess}
                onError={handleMagicLinkError}
                disabled={oauthLoading}
              />
            </div>
          </div>

          {/* Conditionally render Google OAuth section if client ID is configured */}
          {GOOGLE_CLIENT_ID && (
            <>
              <OAuthDivider text="or continue with" />

              {/* Google Sign-In */}
              <GoogleSignInButton
                mode="login"
                onSuccess={handleGoogleSuccess}
                onError={handleGoogleError}
                disabled={oauthLoading}
              />
            </>
          )}

          {/* Security & Privacy Note */}
          <div className="mt-6 text-center">
            <p className="text-xs text-gray-500 flex items-center justify-center gap-1">
              <svg
                className="w-4 h-4 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                />
              </svg>
              Your data is encrypted and secure
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
